import React, { type ReactNode } from 'react';
import {
  Avatar,
  Box,
  Chip,
  ListItemButton,
  ListItemText,
} from '@mui/material';
import { APP_CONFIG } from '../../../config/app.config';

export interface OnboardingEntityListItem {
  id: string;
  primary: string;
  secondary?: string;
  avatarSrc?: string;
  avatarFallback?: ReactNode;
  chipLabel?: string;
  chipColor?: 'default' | 'success' | 'primary' | 'secondary' | 'error' | 'info' | 'warning';
  onClick?: () => void;
}

interface OnboardingEntityListProps {
  items: OnboardingEntityListItem[];
  /** When false, rows are not clickable. */
  interactive?: boolean;
}

const OnboardingEntityList: React.FC<OnboardingEntityListProps> = ({
  items,
  interactive = true,
}) => {
  const colors = APP_CONFIG.ui.theme.colors;

  return (
    <Box
      sx={{
        border: 1,
        borderColor: colors.border,
        borderRadius: '8px',
        overflow: 'hidden',
        width: '100%',
      }}
    >
      {items.map((item) => {
        const content = (
          <>
            <Avatar
              variant="rounded"
              src={item.avatarSrc}
              sx={{
                width: 48,
                height: 48,
                border: 1,
                borderColor: colors.border,
                bgcolor: 'background.paper',
                color: colors.secondaryText,
                fontSize: 16,
                fontWeight: 600,
              }}
            >
              {item.avatarFallback ?? item.primary.charAt(0).toUpperCase()}
            </Avatar>
            <ListItemText primary={item.primary} secondary={item.secondary} />
            {item.chipLabel && (
              <Chip
                label={item.chipLabel}
                color={item.chipColor ?? 'default'}
                size="small"
              />
            )}
          </>
        );

        if (interactive && item.onClick) {
          return (
            <ListItemButton
              key={item.id}
              sx={{
                p: 2,
                gap: 2,
                borderBottom: 1,
                borderColor: colors.border,
                '&:last-child': { borderBottom: 0 },
              }}
              onClick={item.onClick}
            >
              {content}
            </ListItemButton>
          );
        }

        return (
          <Box
            key={item.id}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              p: 2,
              borderBottom: 1,
              borderColor: colors.border,
              '&:last-child': { borderBottom: 0 },
            }}
          >
            {content}
          </Box>
        );
      })}
    </Box>
  );
};

export default OnboardingEntityList;
